'use client';

import { useContext, useEffect, useRef } from 'react';
import useMarketStateStore, {
	MarketStateStore,
	MarketListeningSelection,
} from '../../stores/useMarketStateStore';
import useInterval from '../useInterval';
import { OrderSubscriberContext } from '../../providers/orderSubscriberProvider';
import { useCurrentSlot } from 'src/hooks/useCurrentSlot';
import {
	getLiveOracleData,
	publishMarketLiveUpdate,
} from 'src/stores/marketLiveState';
import {
	ENUM_UTILS,
	MarketId,
	calculateSpreadBidAskMark,
} from '@velocity-exchange/common';
import {
	BigNum,
	DEFAULT_TOP_OF_BOOK_QUOTE_AMOUNTS,
	VelocityClient,
	L2Level,
	L2OrderBook,
	MarketType,
	MMOraclePriceData,
	PERCENTAGE_PRECISION_EXP,
	PRICE_PRECISION_EXP,
	PerpMarketAccount,
	QUOTE_PRECISION_EXP,
	SlotDurationState,
	ZERO,
	getVammL2Generator,
} from '@velocity-exchange/sdk';
import useVelocityClient from '../useVelocityClient';
import { sortBnAsc, sortBnDesc } from '@velocity-exchange/common';
import useMarketsForDlobToTrack from '../useMarketsForDlobToTrack';
import useVelocityStore from '../../stores/VelocityStore/useVelocityStore';
import { PageRoute } from '../../constants/constants';
import { useUncrossOrderbook } from './useUncrossOrderbook';
import useDevStore from '../../stores/useDevStore';
import { VelocityWindow } from 'src/window/velocityWindow';
import useOrderbookDisplayStore, {
	OrderbookSource,
} from 'src/stores/orderbook/useOrderbookDisplayStore';
import { useQueryClient } from '@tanstack/react-query';
import { orderbookQueryKeys } from 'src/hooks/query/orderbook/orderbookQueryKeys';
import { L2OrderbookWithSlot } from 'src/hooks/query/orderbook/types';

const DLOB_REFRESH_RATE_MS = 1000;
// rate of refresh to use for any pages besides trade and overview (where users can trade from) - can play w this
const LAZY_DLOB_REFRESH_RATE_MS = 20000;

const DEFAULT_L2_STATE: L2OrderBook = { asks: [], bids: [] };
const DEFAULT_ORDERBOOK_ROWS = 20;

const getVammL2ForMarket = (
	marketType: MarketType,
	perpMarketAccount: PerpMarketAccount,
	priceData: MMOraclePriceData,
	groupingSize: number,
	slotDurationState: SlotDurationState
): L2OrderBook => {
	if (!ENUM_UTILS.match(marketType, MarketType.PERP) || !perpMarketAccount)
		return DEFAULT_L2_STATE;

	const vammL2Generator = getVammL2Generator({
		marketAccount: perpMarketAccount,
		mmOraclePriceData: priceData,
		numOrders: groupingSize * DEFAULT_ORDERBOOK_ROWS,
		topOfBookQuoteAmounts: DEFAULT_TOP_OF_BOOK_QUOTE_AMOUNTS,
		slotDurationState,
	});

	const vammAsksGenerator = vammL2Generator.getL2Asks();
	const vammBidsGenerator = vammL2Generator.getL2Bids();

	const vammL2 = {
		asks: Array.from(vammAsksGenerator),
		bids: Array.from(vammBidsGenerator),
	};

	return vammL2;
};

/**
 * Merge seperate sources of l2 orderbook together. During the merge it will sort by price (ascending) and merge sources where they share the same price
 * @param l2Data
 * @returns
 */
const mergeL2Data = (l2Data: L2OrderBook[]): L2OrderBook => {
	// Buckets to put bids and asks into when merging
	const askBuckets = new Map<string, L2Level>();
	const bidBuckets = new Map<string, L2Level>();

	const addOrAppendToBucketStorage = (
		key: string,
		currentL2Level: L2Level,
		bucketStorage: Map<string, L2Level>
	) => {
		const currentBucket = bucketStorage.get(key);

		if (currentBucket) {
			currentBucket.size = currentBucket.size.add(currentL2Level.size);
			currentBucket.sources = {
				...currentBucket.sources,
				...currentL2Level.sources,
			};
		} else {
			bucketStorage.set(key, currentL2Level);
		}
	};

	// Place all the bids and asks into the buckets
	for (const bid of l2Data.map((d) => d.bids).flat()) {
		const bidKey = bid.price.toString();
		addOrAppendToBucketStorage(bidKey, bid, bidBuckets);
	}
	for (const ask of l2Data.map((d) => d.asks).flat()) {
		const askKey = ask.price.toString();
		addOrAppendToBucketStorage(askKey, ask, askBuckets);
	}

	// Map the bids and asks back out into l2 orderbook
	const l2Orderbook = {
		bids: Array.from(bidBuckets.values()).sort((bidA, bidB) =>
			sortBnDesc(bidA.price, bidB.price)
		),
		asks: Array.from(askBuckets.values()).sort((askA, askB) =>
			sortBnAsc(askA.price, askB.price)
		),
	};

	return l2Orderbook;
};

/**
 * Gets merged L2 orderbook state for PERP markets (VAMM + DLOB)
 */
export const getPerpMarketL2State = (
	marketsToGetDlobFor: MarketId[],
	newDlob: any,
	currentSlot: number,
	velocityClient: VelocityClient
): L2OrderBook[] => {
	return marketsToGetDlobFor.map((mkt) => {
		if (!ENUM_UTILS.match(mkt.marketType, MarketType.PERP)) {
			return { bids: [], asks: [] };
		}

		const mmOraclePriceData = velocityClient.getMMOracleDataForPerpMarket(
			mkt.marketIndex
		);

		if (!mmOraclePriceData) {
			return { bids: [], asks: [] };
		}

		// Get VAMM L2 state
		const perpMarketAccount = velocityClient?.getPerpMarketAccount(
			mkt.marketIndex
		);

		if (!perpMarketAccount) {
			return { bids: [], asks: [] };
		}

		const vammL2 = getVammL2ForMarket(
			mkt.marketType,
			perpMarketAccount,
			mmOraclePriceData,
			1,
			velocityClient.getStateAccount()
		);

		// Get DLOB L2 state
		const bidsGenerator = newDlob.getRestingLimitBids(
			mkt.marketIndex,
			currentSlot,
			mkt.marketType,
			mmOraclePriceData
		);

		const asksGenerator = newDlob.getRestingLimitAsks(
			mkt.marketIndex,
			currentSlot,
			mkt.marketType,
			mmOraclePriceData
		);

		const dlobBids: L2Level[] = Array.from(bidsGenerator).map((node: any) => {
			const size = node.order
				? node.order.baseAssetAmount.sub(node.order.baseAssetAmountFilled)
				: ZERO;
			return {
				price: node.getPrice(mmOraclePriceData, currentSlot),
				size,
				sources: { dlob: size },
			};
		});

		const dlobAsks: L2Level[] = Array.from(asksGenerator).map((node: any) => {
			const size = node.order
				? node.order.baseAssetAmount.sub(node.order.baseAssetAmountFilled)
				: ZERO;

			return {
				price: node.getPrice(mmOraclePriceData, currentSlot),
				size,
				sources: { dlob: size },
			};
		});

		const dlobL2 = { bids: dlobBids, asks: dlobAsks };

		// Merge VAMM and DLOB
		return mergeL2Data([vammL2, dlobL2]);
	});
};

/**
 * Gets merged L2 orderbook state for SPOT markets (DLOB only).
 */
export const getSpotMarketL2State = (
	marketsToGetDlobFor: MarketId[],
	newDlob: any,
	currentSlot: number,
	velocityClient: VelocityClient
): L2OrderBook[] => {
	return marketsToGetDlobFor.map((mkt) => {
		if (!ENUM_UTILS.match(mkt.marketType, MarketType.SPOT)) {
			return { bids: [], asks: [] };
		}

		// Skip USDC market
		if (mkt.marketIndex === 0) {
			return { bids: [], asks: [] };
		}

		const oraclePriceData = velocityClient.getOracleDataForSpotMarket(
			mkt.marketIndex
		);

		if (!oraclePriceData) {
			return { bids: [], asks: [] };
		}

		// Get DLOB L2 state
		const bidsGenerator = newDlob.getRestingLimitBids(
			mkt.marketIndex,
			currentSlot,
			mkt.marketType,
			oraclePriceData
		);

		const asksGenerator = newDlob.getRestingLimitAsks(
			mkt.marketIndex,
			currentSlot,
			mkt.marketType,
			oraclePriceData
		);

		const dlobBids: L2Level[] = Array.from(bidsGenerator).map((node: any) => {
			const size = node.order
				? node.order.baseAssetAmount.sub(node.order.baseAssetAmountFilled)
				: ZERO;
			return {
				price: node.getPrice(oraclePriceData, currentSlot),
				size,
				sources: { dlob: size },
			};
		});

		const dlobAsks: L2Level[] = Array.from(asksGenerator).map((node: any) => {
			const size = node.order
				? node.order.baseAssetAmount.sub(node.order.baseAssetAmountFilled)
				: ZERO;

			return {
				price: node.getPrice(oraclePriceData, currentSlot),
				size,
				sources: { dlob: size },
			};
		});

		const dlobL2 = { bids: dlobBids, asks: dlobAsks };

		return mergeL2Data([dlobL2]);
	});
};

/**
 * Handles turning on the blockchain order listener and keeping it in sync with the DLOB class when the dlob listening gets enabled.
 * @param enabled
 */
const useSyncMarketDataWithBlockchain = () => {
	const queryClient = useQueryClient();
	const setMarketStateStore = useMarketStateStore((s) => s.set);
	const orderSubscriber = useContext(OrderSubscriberContext)?.orderSubscriber;
	const currentSlot = useCurrentSlot();
	const velocityClient = useVelocityClient();
	const marketsToTrack = useMarketsForDlobToTrack(
		MarketListeningSelection.BLOCKCHAIN
	);
	const shouldBeUsedForOrderbook = useOrderbookDisplayStore(
		(s) => s.orderbookSource === OrderbookSource.BLOCKCHAIN
	);
	const setOrderbookStoreState = useOrderbookDisplayStore((s) => s.set);
	const uncrossDisabled_ = useDevStore((s) => s.dlobSettings.disableUncross);
	const uncrossDisabled = useRef(uncrossDisabled_);
	useEffect(() => {
		uncrossDisabled.current = uncrossDisabled_;
	}, [uncrossDisabled_]);

	const uncross = useUncrossOrderbook();

	const currentPage = useVelocityStore((s) => s.currentPageRoute);
	const refreshRateToUse =
		!currentPage ||
		ENUM_UTILS.match(currentPage, PageRoute.portfolio) ||
		ENUM_UTILS.match(currentPage, PageRoute.trade)
			? DLOB_REFRESH_RATE_MS
			: LAZY_DLOB_REFRESH_RATE_MS;
	const bulkAccountLoader = useVelocityStore((s) => s.currentBulkAccountLoader);

	const enabled = marketsToTrack.totalMarkets > 0;

	const allMarketsToTrack = marketsToTrack.allMarkets;

	useEffect(() => {
		if (!orderSubscriber) return;

		if (enabled) {
			// Turn the blockchain listener on
			orderSubscriber.subscribe();
		} else {
			// Turn the blockchain listener off
			orderSubscriber.usersAccounts?.clear();
			orderSubscriber.unsubscribe();
		}

		return () => {
			// Unsubscribe at end of hook to be safe
			orderSubscriber.usersAccounts?.clear();
			orderSubscriber.unsubscribe();
		};
	}, [enabled, orderSubscriber]);

	useInterval(() => {
		if (!enabled) return;

		if (VelocityWindow.BREAK_BLOCKCHAIN_DLOB_FETCHING) {
			// Dev setting for testing fallback logic
			return;
		}

		(async () => {
			if (!orderSubscriber || !velocityClient) return;

			// All markets except USDC SPOT market
			const marketsToGetDlobFor = allMarketsToTrack;

			const newDlob = await orderSubscriber.getDLOB(currentSlot);

			const newL2State: MarketStateStore['marketDataState'] = {};
			// Oracle is published by the rpc adapter; mark is derived here. Both
			// feed the module and the pricing cache below.
			const priceByMarket: Record<
				string,
				{ oraclePrice: BigNum; markPrice: BigNum }
			> = {};

			// Get merged L2 state for PERP markets
			const perpL2State = getPerpMarketL2State(
				marketsToGetDlobFor,
				newDlob,
				currentSlot,
				velocityClient
			);

			// Get merged L2 state for SPOT markets
			const spotL2State = getSpotMarketL2State(
				marketsToGetDlobFor,
				newDlob,
				currentSlot,
				velocityClient
			);

			// Combine results based on market type
			const mergedDlobState = marketsToGetDlobFor.map((mkt, index) => {
				if (ENUM_UTILS.match(mkt.marketType, MarketType.PERP)) {
					return perpL2State[index];
				} else {
					return spotL2State[index];
				}
			});

			// Fill the new L2 state with the merged data
			marketsToGetDlobFor.forEach((mktId, index) => {
				const dlobStateForMarket = mergedDlobState[index];

				const oracleData = getLiveOracleData(mktId, 'rpc');

				if (!oracleData?.raw) return;

				// TODO : This method loops through all orders, should be able to assume that they are sorted here and just pick the ones off the top (applies to the other places this is used too)
				const spreadBidAskMark = calculateSpreadBidAskMark(
					dlobStateForMarket,
					oracleData.raw.price
				);

				const orderbookToUse = uncrossDisabled.current
					? dlobStateForMarket
					: uncross.uncrossL2Orderbook(
							dlobStateForMarket,
							oracleData.raw,
							mktId
						);

				const markPrice = BigNum.from(
					spreadBidAskMark.markPrice,
					PRICE_PRECISION_EXP
				);
				const updateSlot = bulkAccountLoader?.mostRecentSlot ?? 0;

				// Oracle for this market is already published by the rpc adapter;
				// here we contribute the DLOB-derived mark price to the module.
				publishMarketLiveUpdate('dlob', mktId, {
					mark: { price: markPrice, slot: updateSlot },
				});

				priceByMarket[mktId.key] = { oraclePrice: oracleData.price, markPrice };

				newL2State[mktId.key] = {
					marketId: mktId,
					orderbook: orderbookToUse,
					derivedState: {
						bestBid: BigNum.from(
							spreadBidAskMark.bestBidPrice,
							PRICE_PRECISION_EXP
						),
						bestAsk: BigNum.from(
							spreadBidAskMark.bestAskPrice,
							PRICE_PRECISION_EXP
						),
						updateSlot,
					},
				};
			});

			// Update the store state with the new L2 state
			setMarketStateStore((s) => {
				for (const market of marketsToGetDlobFor) {
					s.marketDataState[market.key] = newL2State[market.key];
				}
				s.latestUpdateSlot = Math.max(
					s.latestUpdateSlot,
					bulkAccountLoader?.mostRecentSlot ?? 0
				);
			});

			if (shouldBeUsedForOrderbook) {
				const currentMarketId = useOrderbookDisplayStore.getState().marketId;
				const selectedMarketData = Object.values(newL2State).find(
					(marketState) =>
						currentMarketId
							? marketState.marketId.equals(currentMarketId)
							: false
				);

				if (selectedMarketData) {
					const marketKey = selectedMarketData.marketId.key;
					const { isPerp, marketIndex } = selectedMarketData.marketId;
					// Same oracles getPerpMarketL2State / getSpotMarketL2State price levels with
					const bookOraclePrice = (
						isPerp
							? velocityClient.getMMOracleDataForPerpMarket(marketIndex)
							: velocityClient.getOracleDataForSpotMarket(marketIndex)
					)?.price;
					const l2OrderbookWithSlot: L2OrderbookWithSlot = {
						...selectedMarketData.orderbook,
						slot: bulkAccountLoader?.mostRecentSlot ?? 0,
						oraclePrice: bookOraclePrice
							? BigNum.from(bookOraclePrice, PRICE_PRECISION_EXP)
							: undefined,
					};

					const oraclePriceData = selectedMarketData.marketId.isPerp
						? velocityClient.getOracleDataForPerpMarket(
								selectedMarketData.marketId.marketIndex
							)
						: velocityClient.getOracleDataForSpotMarket(
								selectedMarketData.marketId.marketIndex
							);

					if (oraclePriceData) {
						const spreadBidAskMark = calculateSpreadBidAskMark(
							selectedMarketData.orderbook,
							oraclePriceData?.price
						);

						queryClient.setQueryData(
							orderbookQueryKeys.l2Orderbook(marketKey),
							l2OrderbookWithSlot
						);
						queryClient.setQueryData(
							orderbookQueryKeys.marketPriceState(marketKey),
							{
								bestAsk: selectedMarketData.derivedState.bestAsk,
								bestBid: selectedMarketData.derivedState.bestBid,
								markPrice: BigNum.from(
									spreadBidAskMark.markPrice,
									PRICE_PRECISION_EXP
								),
								spreadPct: BigNum.from(
									spreadBidAskMark.spreadPct,
									PERCENTAGE_PRECISION_EXP
								),
								spreadQuote: BigNum.from(
									spreadBidAskMark.spreadQuote,
									QUOTE_PRECISION_EXP
								),
								slot: selectedMarketData.marketSlot,
							}
						);

						setOrderbookStoreState((s) => {
							s.loading = false;
						});
					}
				}
			}
		})();
	}, refreshRateToUse);
};

export default useSyncMarketDataWithBlockchain;
